<div class="cd-page">
  <app-counselor-nav></app-counselor-nav>

  <main class="cc-main" aria-label="Calendar">
    <!-- TOP BAR -->
    <div class="cc-topbar">
      <div class="cc-topbar-left">
        <button
          mat-stroked-button
          class="cc-today-btn"
          (click)="goToToday()"
          aria-label="Go to today"
          data-testid="today-btn"
        >
          Today
        </button>
        <div class="cc-topbar-divider" aria-hidden="true"></div>
        <mat-button-toggle-group
          [value]="viewMode()"
          class="cc-toggle"
          (change)="setView($event.value)"
          aria-label="Calendar view"
        >
          <mat-button-toggle value="month" data-testid="view-month">Month</mat-button-toggle>
          <mat-button-toggle value="week" data-testid="view-week">Week</mat-button-toggle>
          <mat-button-toggle value="day" data-testid="view-day">Day</mat-button-toggle>
        </mat-button-toggle-group>
      </div>

      <div class="cc-nav-pill">
        <button
          mat-icon-button
          class="cc-nav-btn"
          (click)="prev()"
          [attr.aria-label]="'Previous ' + viewMode()"
          data-testid="prev-btn"
        >
          <mat-icon aria-hidden="true">chevron_left</mat-icon>
        </button>
        <span class="cc-label" aria-live="polite" aria-atomic="true">
          @if (viewMode() === 'week') {
            {{ weekRangeLabel() }}
          } @else if (viewMode() === 'day') {
            {{ dayViewLabel() }}
          } @else {
            {{ monthLabel() }}
          }
        </span>
        <button
          mat-icon-button
          class="cc-nav-btn"
          (click)="next()"
          [attr.aria-label]="'Next ' + viewMode()"
          data-testid="next-btn"
        >
          <mat-icon aria-hidden="true">chevron_right</mat-icon>
        </button>
      </div>

      <div class="cc-topbar-right">
        <button
          mat-flat-button
          class="cc-add-btn"
          aria-label="Add event"
          (click)="openAddEventDialog()"
          data-testid="add-event-btn"
        >
          <mat-icon aria-hidden="true">add</mat-icon> Add event
        </button>
      </div>
    </div>

    <!-- WEEK VIEW -->
    @if (viewMode() === 'week') {
      <div class="cc-week-shell" role="grid" aria-label="Week view">
        <div class="cc-day-headers" role="row">
          @for (day of weekDays(); track day.toDateString()) {
            <div
              class="cc-day-hdr"
              [class.cc-today-hdr]="isToday(day)"
              (click)="selectDay(day)"
              role="columnheader"
              tabindex="0"
              (keydown.enter)="selectDay(day)"
              (keydown.space)="selectDay(day)"
              [attr.aria-label]="
                dayShort(day) +
                ' ' +
                day.getDate() +
                (isToday(day) ? ', today' : '') +
                (eventsForDay(day).length > 0 ? ', ' + eventsForDay(day).length + ' events' : '')
              "
              matRipple
            >
              <span class="cc-day-name" aria-hidden="true">{{ dayShort(day) }}</span>
              <span class="cc-day-num" aria-hidden="true" [class.cc-today-num]="isToday(day)">{{
                day.getDate()
              }}</span>
              @if (eventsForDay(day).length > 0) {
                <span class="cc-dot" aria-hidden="true"></span>
              }
            </div>
          }
        </div>

        <div class="cc-week-body" #weekBody>
          <div class="cc-time-col" aria-hidden="true">
            @for (h of hours; track h) {
              <div class="cc-time-label">{{ hourLabel(h) }}</div>
            }
          </div>

          <div class="cc-day-cols">
            @for (day of weekDays(); track day.toDateString()) {
              <div
                class="cc-day-col"
                role="gridcell"
                [class.cc-today-col]="isToday(day)"
                [attr.aria-label]="dayShort(day) + ' ' + day.getDate()"
              >
                @for (h of hours; track h) {
                  <div class="cc-cell" aria-hidden="true"></div>
                }
                @let weekLayout = dayLayout(day);
                @for (tile of weekLayout.tiles; track tile.event.key) {
                  <div
                    class="cc-ev cc-ev-{{ tile.event.type }}"
                    [class.cc-ev-today]="isToday(day)"
                    [class.cc-ev-narrow]="tile.cols >= 3"
                    role="button"
                    tabindex="0"
                    [attr.aria-label]="
                      tile.event.title +
                      ', ' +
                      hourLabel(tile.event.startHour) +
                      ' to ' +
                      hourLabel(tile.event.endHour)
                    "
                    [style.top.px]="eventTop(tile.event)"
                    [style.height.px]="eventHeight(tile.event)"
                    [style.left]="eventLeft(tile.col, tile.cols)"
                    [style.width]="eventWidth(tile.cols)"
                    [matTooltip]="
                      tile.event.title +
                      ' · ' +
                      timeLabel(tile.event.startHour, tile.event.startMinute) +
                      '–' +
                      timeLabel(tile.event.endHour, tile.event.endMinute)
                    "
                    (click)="openEventDialog(tile.event)"
                    (keydown.enter)="openEventDialog(tile.event)"
                    (keydown.space)="openEventDialog(tile.event)"
                    [attr.data-testid]="'event-' + tile.event.id"
                  >
                    <span class="cc-ev-title">{{ tile.event.title }}</span>
                    @if (!isCompact(tile.event)) {
                      <span class="cc-ev-time"
                        >{{ timeLabel(tile.event.startHour, tile.event.startMinute)
                        }}{{
                          tile.event.raw.end_time
                            ? ' – ' + timeLabel(tile.event.endHour, tile.event.endMinute)
                            : ''
                        }}</span
                      >
                    }
                  </div>
                }
                @for (over of weekLayout.overflows; track $index) {
                  <div
                    class="cc-ev cc-ev-more"
                    [class.cc-ev-today]="isToday(day)"
                    role="button"
                    tabindex="0"
                    [attr.aria-label]="over.hidden.length + ' more overlapping events'"
                    [style.top.px]="minsTop(over.start)"
                    [style.height.px]="minsHeight(over.start, over.end)"
                    [style.left]="eventLeft(over.col, over.cols)"
                    [style.width]="eventWidth(over.cols)"
                    [matTooltip]="'Show ' + over.hidden.length + ' more events'"
                    (click)="openOverflow(over)"
                    (keydown.enter)="openOverflow(over)"
                    (keydown.space)="openOverflow(over)"
                    [attr.data-testid]="'week-overflow-' + $index"
                  >
                    <span class="cc-ev-title">+{{ over.hidden.length }} more</span>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      </div>
    }

    <!-- MONTH VIEW -->
    @if (viewMode() === 'month') {
      <div class="cc-month-shell" role="grid" aria-label="Month view">
        <div class="cc-month-dow" role="row">
          @for (d of monthDayNums; track d) {
            <div class="cc-dow-label" role="columnheader" aria-label="{{ d }}">{{ d }}</div>
          }
        </div>
        <div class="cc-month-grid">
          @for (cell of monthGrid(); track $index) {
            <div
              class="cc-month-cell"
              role="gridcell"
              [class.cc-month-empty]="!cell"
              [class.cc-month-today]="cell && isToday(cell)"
              [attr.aria-label]="
                cell
                  ? cell.getDate() +
                    (isToday(cell) ? ', today' : '') +
                    (eventsForDay(cell).length > 0
                      ? ', ' + eventsForDay(cell).length + ' events'
                      : ', no events')
                  : null
              "
              [attr.aria-disabled]="!cell ? 'true' : null"
              [attr.tabindex]="cell ? 0 : null"
              (click)="cell && selectDay(cell)"
              (keydown.enter)="cell && selectDay(cell)"
              (keydown.space)="cell && selectDay(cell)"
            >
              @if (cell) {
                <span
                  class="cc-month-num"
                  aria-hidden="true"
                  [class.cc-month-today-num]="isToday(cell)"
                >
                  {{ cell.getDate() }}
                </span>
                <div class="cc-month-events" aria-hidden="true">
                  @for (ev of eventsForDay(cell).slice(0, 2); track ev.key) {
                    <div
                      class="cc-month-ev cc-month-ev-{{ ev.type }}"
                      (click)="openEventDialog(ev); $event.stopPropagation()"
                      [attr.data-testid]="'month-event-' + ev.id"
                    >
                      {{ ev.title }}
                    </div>
                  }
                  @if (eventsForDay(cell).length > 2) {
                    <div class="cc-month-more">+{{ eventsForDay(cell).length - 2 }} more</div>
                  }
                </div>
              }
            </div>
          }
        </div>
      </div>
    }

    <!-- DAY VIEW -->
    @if (viewMode() === 'day') {
      <div class="cc-day-shell" role="region" [attr.aria-label]="'Day view — ' + dayViewLabel()">
        <div
          class="cc-day-hdr-single"
          [class.cc-today-hdr]="isToday(selectedDay())"
          aria-hidden="true"
        >
          <span class="cc-day-name">{{ dayShort(selectedDay()) }}</span>
          <span class="cc-day-num-big" [class.cc-today-num]="isToday(selectedDay())">
            {{ selectedDay().getDate() }}
          </span>
        </div>
        <div class="cc-day-body" #dayBody>
          <div class="cc-time-col" aria-hidden="true">
            @for (h of hours; track h) {
              <div class="cc-time-label">{{ hourLabel(h) }}</div>
            }
          </div>
          <div
            class="cc-day-single-col"
            role="list"
            [attr.aria-label]="'Events for ' + dayViewLabel()"
          >
            @for (h of hours; track h) {
              <div class="cc-cell" aria-hidden="true"></div>
            }
            @let dayLayoutData = dayLayout(selectedDay());
            @for (tile of dayLayoutData.tiles; track tile.event.key) {
              <div
                class="cc-ev cc-ev-{{ tile.event.type }} cc-ev-day"
                [class.cc-ev-narrow]="tile.cols >= 3"
                role="button"
                tabindex="0"
                [attr.aria-label]="
                  tile.event.title +
                  ', ' +
                  timeLabel(tile.event.startHour, tile.event.startMinute) +
                  ' to ' +
                  timeLabel(tile.event.endHour, tile.event.endMinute)
                "
                [style.top.px]="eventTop(tile.event)"
                [style.height.px]="eventHeight(tile.event)"
                [style.left]="eventLeft(tile.col, tile.cols)"
                [style.width]="eventWidth(tile.cols)"
                [matTooltip]="
                  tile.event.title +
                  ' · ' +
                  timeLabel(tile.event.startHour, tile.event.startMinute) +
                  '–' +
                  timeLabel(tile.event.endHour, tile.event.endMinute)
                "
                (click)="openEventDialog(tile.event)"
                (keydown.enter)="openEventDialog(tile.event)"
                (keydown.space)="openEventDialog(tile.event)"
                [attr.data-testid]="'day-event-' + tile.event.id"
              >
                <span class="cc-ev-title">{{ tile.event.title }}</span>
                @if (!isCompact(tile.event)) {
                  <span class="cc-ev-time"
                    >{{ timeLabel(tile.event.startHour, tile.event.startMinute) }} –
                    {{ timeLabel(tile.event.endHour, tile.event.endMinute) }}</span
                  >
                }
              </div>
            }
            @for (over of dayLayoutData.overflows; track $index) {
              <div
                class="cc-ev cc-ev-more cc-ev-day"
                role="button"
                tabindex="0"
                [attr.aria-label]="over.hidden.length + ' more overlapping events'"
                [style.top.px]="minsTop(over.start)"
                [style.height.px]="minsHeight(over.start, over.end)"
                [style.left]="eventLeft(over.col, over.cols)"
                [style.width]="eventWidth(over.cols)"
                [matTooltip]="'Show ' + over.hidden.length + ' more events'"
                (click)="openOverflow(over)"
                (keydown.enter)="openOverflow(over)"
                (keydown.space)="openOverflow(over)"
                [attr.data-testid]="'day-overflow-' + $index"
              >
                <span class="cc-ev-title">+{{ over.hidden.length }} more</span>
              </div>
            }
            @if (eventsForDay(selectedDay()).length === 0) {
              <div class="cc-day-empty" role="status">
                <mat-icon aria-hidden="true">event_available</mat-icon>
                <span>Nothing scheduled for this day</span>
              </div>
            }
          </div>
        </div>
      </div>
    }
  </main>
</div>
